import { useMemo, useState } from "react";
import { useEvents } from "@/hooks/useConflictData";
import { useAuth } from "@/hooks/useAuth";
import { Lock, TrendingUp, TrendingDown, Minus, Globe, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

const EVENT_SEVERITY: Record<string, number> = {
  missile: 5, explosion: 4, air_raid: 3, drone: 3, movement: 1,
};

type CountryEntry = {
  country: string;
  cii: number;
  eventCount: number;
  recentCount: number;
  dominantType: string;
  trend: "up" | "down" | "stable";
};

const getCIILevel = (cii: number) => {
  if (cii >= 80) return { label: "EXTREME", color: "text-red-400 bg-red-500/15 border-red-500/30" };
  if (cii >= 60) return { label: "HIGH", color: "text-orange-400 bg-orange-500/15 border-orange-500/30" };
  if (cii >= 40) return { label: "ELEVATED", color: "text-yellow-400 bg-yellow-500/15 border-yellow-500/30" };
  if (cii >= 20) return { label: "MODERATE", color: "text-blue-400 bg-blue-500/15 border-blue-500/30" };
  return { label: "LOW", color: "text-green-400 bg-green-500/15 border-green-500/30" };
};

const CountryInstability = () => {
  const { isPremium } = useAuth();
  const { data: events = [], isLoading } = useEvents(undefined, isPremium);
  const [search, setSearch] = useState("");

  const countries = useMemo<CountryEntry[]>(() => {
    const now = Date.now();
    const byCountry: Record<string, { events: any[]; recentEvents: any[] }> = {};

    events.forEach((e) => {
      if (!e.country) return;
      if (!byCountry[e.country]) byCountry[e.country] = { events: [], recentEvents: [] };
      byCountry[e.country].events.push(e);
      const ageH = (now - new Date(e.occurred_at).getTime()) / 3600000;
      if (ageH < 6) byCountry[e.country].recentEvents.push(e);
    });

    return Object.entries(byCountry)
      .map(([country, { events: evts, recentEvents }]) => {
        // CII = weighted sum of event severity × recency factor, normalized to 0-100
        let rawScore = 0;
        const typeCounts: Record<string, number> = {};

        evts.forEach((e) => {
          const severity = EVENT_SEVERITY[e.event_type] || 1;
          const ageH = (now - new Date(e.occurred_at).getTime()) / 3600000;
          const recencyWeight = Math.max(0, 1 - ageH / 168); // decay over 7 days
          rawScore += severity * (1 + recencyWeight);
          typeCounts[e.event_type] = (typeCounts[e.event_type] || 0) + 1;
        });

        // Normalize: cap at 100, log scale for spread
        const cii = Math.min(100, Math.round(Math.log2(rawScore + 1) * 12));

        const dominantType = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "unknown";

        // Trend: compare last 6h vs previous 6h
        const prev6h = evts.filter((e) => {
          const ageH = (now - new Date(e.occurred_at).getTime()) / 3600000;
          return ageH >= 6 && ageH < 12;
        });

        let trend: "up" | "down" | "stable" = "stable";
        if (recentEvents.length > prev6h.length * 1.5) trend = "up";
        else if (recentEvents.length < prev6h.length * 0.5) trend = "down";

        return { country, cii, eventCount: evts.length, recentCount: recentEvents.length, dominantType, trend };
      })
      .sort((a, b) => b.cii - a.cii)
      .slice(0, 20);
  }, [events]);

  const filtered = useMemo(() => {
    if (!search.trim()) return countries;
    return countries.filter((c) => c.country.toLowerCase().includes(search.toLowerCase()));
  }, [countries, search]);

  const TrendIcon = ({ trend }: { trend: string }) => {
    if (trend === "up") return <TrendingUp className="h-3 w-3 text-red-400" />;
    if (trend === "down") return <TrendingDown className="h-3 w-3 text-green-400" />;
    return <Minus className="h-3 w-3 text-muted-foreground" />;
  };

  return (
    <section id="country-instability" className="py-8">
      <div className="container mx-auto px-4">
        <div className="mb-6 flex items-center gap-3">
          <Globe className="h-5 w-5 text-primary" />
          <h2 className="font-mono text-lg font-bold uppercase tracking-wider text-foreground">
            Country Instability Index
          </h2>
        </div>

        <div className="mb-4 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search country... (e.g. Turkey, Ukraine)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-background text-sm"
          />
        </div>

        <div className="grid gap-2">
          {/* Header */}
          <div className="grid grid-cols-[1fr_80px_60px_80px_60px] gap-2 px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground border-b border-border">
            <span>Country</span>
            <span className="text-center">CII</span>
            <span className="text-center">Events</span>
            <span className="text-center">Type</span>
            <span className="text-center">Trend</span>
          </div>

          {filtered.map((c, i) => {
            const level = getCIILevel(c.cii);
            const isBlurred = !isPremium && i >= 5;

            return (
              <div
                key={c.country}
                className={`grid grid-cols-[1fr_80px_60px_80px_60px] gap-2 items-center px-3 py-2 rounded-md transition-colors hover:bg-accent/50 ${isBlurred ? "blur-sm select-none" : ""}`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-bold text-foreground">{i + 1}.</span>
                  <span className="text-sm text-foreground truncate">{c.country}</span>
                </div>

                <div className="flex justify-center">
                  <Badge variant="outline" className={`text-[10px] font-mono font-bold px-1.5 py-0 ${level.color}`}>
                    {c.cii}
                  </Badge>
                </div>

                <span className="text-center text-xs font-mono text-muted-foreground">{c.eventCount}</span>

                <span className="text-center text-[10px] font-mono uppercase text-muted-foreground">{c.dominantType}</span>

                <div className="flex justify-center">
                  <TrendIcon trend={c.trend} />
                </div>
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="p-8 text-center text-muted-foreground text-sm">
              {search ? "No countries match your search." : isLoading ? "Calculating instability indices..." : "No recent event data available. Indices will update as new reports arrive."}
            </div>
          )}
        </div>

        {!isPremium && (
          <div className="mt-3 rounded-md border border-primary/20 bg-primary/5 p-3 text-center">
            <p className="text-xs text-muted-foreground">
              <Lock className="mr-1 inline h-3 w-3" />
              Free users see top 5 countries. <span className="text-primary font-semibold cursor-pointer">Upgrade to Premium</span> for full index with historical trends.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default CountryInstability;
